/* import __COLOCATED_TEMPLATE__ from './list-sheet.hbs'; */
/* RESPONSIBLE TEAM: team-conversations */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type User from 'embercom/objects/inbox/user';
import type Attribute from 'embercom/objects/inbox/attribute';
// @ts-ignore
import type InboxSidebarService from 'embercom/services/inbox-sidebar-service';
import { type SidebarSectionComponentArgs } from 'embercom/lib/inbox2/sidebar-types';
import { action } from '@ember/object';
import { postRequest } from 'embercom/lib/inbox/requests';
import type Session from 'embercom/services/session';
import type IntlService from 'embercom/services/intl';

interface NotificationsService {
  notifyError(message: string): void;
  notifyConfirmation(message: string): void;
}

type Args = SidebarSectionComponentArgs<{
  user: User;
  potentialDuplicates: User[];
  userAttributes: Attribute[];
  toggleUserDetailsPin: (pinnedAttrsKeys: string[]) => unknown;
  collapseSheet: () => unknown;
  refreshDuplicates?: () => void;
}>;

interface Signature {
  Args: Args;
}

export default class PotentialDuplicatesListSheet extends Component<Signature> {
  @service declare inboxSidebarService: InboxSidebarService;
  @service declare session: Session;
  @service declare intl: IntlService;
  @service declare notificationsService: NotificationsService;

  @action async mergeUserAndLead(duplicate: User) {
    // The merge is always Lead → User: the Lead (source) is deleted,
    // the User (target) keeps the data. Determine which is which based on roles,
    // since we can be viewing either the Lead's or the User's conversation.
    let source_user_id;
    let target_user_id;
    if (this.args.user.hasLeadRole && duplicate.hasUserRole) {
      source_user_id = this.args.user.id;
      target_user_id = duplicate.id;
    } else if (this.args.user.hasUserRole && duplicate.hasLeadRole) {
      source_user_id = duplicate.id;
      target_user_id = this.args.user.id;
    } else {
      this.notificationsService.notifyError(
        this.intl.t('inbox.conversation-details-sidebar.potential-duplicates.merge-error'),
      );
    }

    if (source_user_id && target_user_id) {
      try {
        await postRequest(`/ember/users/merge_lead_into_user?app_id=${this.session.workspace.id}`, {
          target_user_id,
          source_user_id,
        });
      } catch (e: unknown) {
        this.notificationsService.notifyError(
          this.intl.t('inbox.conversation-details-sidebar.potential-duplicates.merge-error'),
        );
      }
    }

    this.args.refreshDuplicates?.();
    this.inboxSidebarService.onExpandPotentialDuplicates(null, true);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationDetailsSidebar::SidebarSections::PotentialDuplicates::ListSheet': typeof PotentialDuplicatesListSheet;
    'inbox2/conversation-details-sidebar/sidebar-sections/potential-duplicates/list-sheet': typeof PotentialDuplicatesListSheet;
  }
}
